import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Eye, EyeOff, ShieldCheck, Handshake, Sparkles, Check } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import AuthSocial from "./AuthSocial";
import { useAuth } from "./authApi";

const HERO = `${process.env.REACT_APP_BACKEND_URL}/api/media/assets/eco_poster_6b26f458140c5c28.jpeg`;

const ADVANTAGES = [
  { icon: Eye, title: "Biens off-market exclusifs", desc: "Des opportunités confidentielles, avant toute diffusion publique." },
  { icon: ShieldCheck, title: "En toute discrétion", desc: "Acheter ou vendre, accompagné par un réseau de confiance." },
  { icon: Sparkles, title: "Rapprochement intelligent", desc: "L'IA relie chaque projet aux bonnes opportunités." },
  { icon: Handshake, title: "Un accompagnement humain", desc: "Des conseillers dédiés, du premier échange à la signature." },
];

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function MemberLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/mon-compte");
    } catch (e) { setErr(e.message || "Identifiants incorrects."); }
    setLoading(false);
  };

  return (
    <PlatformLayout searchDock={false} solidHeader>
      <div className="relative min-h-screen bg-[#0d0d10] overflow-hidden flex items-center">
        {/* halo doré discret */}
        <div aria-hidden className="pointer-events-none absolute -top-32 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#C19A6B]/10 blur-[120px]" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#C19A6B]/[0.06] blur-[120px]" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          {/* Colonne gauche — présentation */}
          <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7 order-2 lg:order-1 h-full flex flex-col" data-testid="login-presentation">
            <motion.div variants={item} className="relative flex-1 min-h-[260px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={HERO} alt="L'équipe et le réseau OFF MARKETTE" className="absolute inset-0 w-full h-full object-cover object-[50%_45%]" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,5,5,0.74) 0%, rgba(5,5,5,0.38) 55%, rgba(5,5,5,0.18) 100%)" }} />
              <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#C19A6B] font-semibold mb-3">Notre réseau</p>
                <p className="text-white text-xl sm:text-2xl lg:text-3xl leading-snug max-w-lg font-light">
                  Que vous achetiez ou vendiez, accédez à un réseau immobilier confidentiel, augmenté par l'intelligence artificielle.
                </p>
              </div>
            </motion.div>

            <motion.ul variants={item} className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
              {ADVANTAGES.map((a) => (
                <li key={a.title} className="flex items-start gap-3" data-testid={`login-adv-${a.title.toLowerCase().split(" ")[0]}`}>
                  <span className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-[#C19A6B]/12 border border-[#C19A6B]/25 flex items-center justify-center text-[#C19A6B]"><a.icon className="w-4 h-4" /></span>
                  <div>
                    <div className="text-[14px] font-medium text-white leading-tight">{a.title}</div>
                    <div className="text-[12.5px] text-zinc-400 leading-snug mt-0.5">{a.desc}</div>
                  </div>
                </li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Colonne droite — connexion */}
          <div className="lg:col-span-5 order-1 lg:order-2 h-full flex">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="relative w-full max-w-md mx-auto h-full flex flex-col justify-center bg-[#141416]/85 backdrop-blur-2xl border border-white/10 rounded-2xl p-7 sm:p-9 shadow-2xl"
              data-testid="member-login"
            >
              <div className="mb-7">
                <div className="w-11 h-11 rounded-full bg-[#C19A6B]/15 border border-[#C19A6B]/30 flex items-center justify-center mb-4"><LogIn className="w-5 h-5 text-[#C19A6B]" /></div>
                <h1 className="calli text-4xl sm:text-5xl text-white" data-testid="login-title">Bienvenue</h1>
                <p className="text-sm text-zinc-400 mt-2">Connectez-vous à votre espace Off Market — acquéreurs, vendeurs et professionnels.</p>
              </div>

              <form onSubmit={submit} className="space-y-3.5">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" data-testid="login-email"
                  className="w-full h-12 bg-black/40 border border-white/10 rounded-xl px-4 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors" />
                <div className="relative">
                  <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" data-testid="login-password"
                    className="w-full h-12 bg-black/40 border border-white/10 rounded-xl px-4 pr-11 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors" />
                  <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-toggle-password" aria-label={show ? "Masquer" : "Afficher"} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#C19A6B] transition-colors">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <a href="mailto:vincent.marette@safti.fr?subject=Réinitialisation%20de%20mot%20de%20passe" data-testid="login-forgot" className="text-xs text-[#C19A6B] hover:text-[#A88354] font-medium transition-colors">Mot de passe oublié ?</a>
                </div>
                {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
                <button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full h-12 bg-[#C19A6B] hover:bg-[#A88354] text-black font-semibold uppercase tracking-wider text-sm rounded-xl transition-all shadow-lg hover:shadow-[0_0_20px_rgba(193,154,107,0.4)] disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? "Connexion…" : <><Check className="w-4 h-4" /> Se connecter</>}
                </button>
              </form>

              <div className="mt-5">
                <AuthSocial redirectPath="/mon-compte" />
              </div>

              <p className="text-center text-sm text-zinc-500 mt-6">Pas encore membre ? <Link to="/inscription" className="text-[#C19A6B] hover:underline">Devenir partenaire</Link></p>
            </motion.div>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
